import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./leaderboard.css";
import { fetchReport } from "../../functions/fetchReport";

function themeColorToCss(val) {
  if (val == null || val === "") return undefined;
  const c = String(val).trim();
  return c || undefined;
}

function timeToMss(raw) {
  if (!raw) return "00:00";
  const s = String(raw).trim();
  if (!s) return "00:00";
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.some((x) => !Number.isFinite(x))) return "00:00";
  let total = 0;
  if (parts.length === 3) total = parts[0] * 3600 + parts[1] * 60 + parts[2];
  else if (parts.length === 2) total = parts[0] * 60 + parts[1];
  else total = parts[0];
  total = Math.max(0, Math.floor(total));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus, currentTheme } = useSelector((state) => state.theme);
  const authUser = useSelector((state) => state.auth?.user);

  const [top, setTop] = useState([]);
  const [me, setMe] = useState(null);
  const [meReportFallback, setMeReportFallback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  // Match reference UI: apply subtle blur during leaderboard view
  useEffect(() => {
    const body = document.body;
    const prev = body.style.getPropertyValue("backdrop-filter");
    body.style.setProperty("backdrop-filter", "blur(9px)");
    return () => {
      if (prev) body.style.setProperty("backdrop-filter", prev);
      else body.style.removeProperty("backdrop-filter");
    };
  }, []);

  useEffect(() => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/getLeaderboard`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: authUser?.userId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success) {
          setTop(Array.isArray(data.top) ? data.top : []);
          setMe(data.me || null);
        } else {
          setError(data.error || data.message || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, authUser?.userId]);

  useEffect(() => {
    if (!adminToken || !authUser?.userId) return;
    if (me || (Array.isArray(top) && top.length > 0)) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchReport({ userId: authUser.userId, token: adminToken, gameId: authUser?.gameId || null });
        if (cancelled) return;
        setMeReportFallback(resp?.report || null);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, authUser?.userId, authUser?.gameId, me, top]);

  const titleColor =
    themeColorToCss(themeData?.landing_page_title_color ?? themeData?.landing_page_title_colour) ||
    themeColorToCss(themeData?.text_color) ||
    "#111";
  const cellColor = themeColorToCss(themeData?.text_color) || "#111";

  const rows = useMemo(() => {
    const out = [...(Array.isArray(top) ? top : [])];
    if (me && !out.some((r) => String(r.userId) === String(me.userId))) out.push(me);
    if (!me && out.length === 0 && meReportFallback && authUser?.userId) {
      out.push({
        rank: "—",
        userId: authUser.userId,
        name: authUser?.name || authUser?.email || "You",
        points: meReportFallback.total_score ?? meReportFallback.points ?? 0,
        time: meReportFallback.time ?? "00:00",
      });
    }
    return out;
  }, [top, me, meReportFallback, authUser?.userId, authUser?.name, authUser?.email]);
  const meUserId = String(authUser?.userId ?? "");

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-page__inner">
        <div className="leaderboard-glass">
          <header className="leaderboard-header">
            <span className="leaderboard-trophy" aria-hidden="true">
              🏆
            </span>
            <h1 className="leaderboard-title" style={{ color: titleColor }}>
              LEADERBOARD
            </h1>
            <span className="leaderboard-trophy" aria-hidden="true">
              🏆
            </span>
          </header>

          {themeStatus === "loading" && !themeData ? (
            <p className="leaderboard-status" style={{ color: cellColor }}>
              Loading theme…
            </p>
          ) : null}

          {loading ? (
            <p className="leaderboard-status" style={{ color: cellColor }}>
              Loading scores…
            </p>
          ) : error ? (
            <p className="leaderboard-status leaderboard-status--error">{error}</p>
          ) : (
            <div className="leaderboard-table-wrap">
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th className="leaderboard-table__rank" style={{ color: titleColor }}>
                      SR.
                    </th>
                    <th className="leaderboard-table__name-col" style={{ color: titleColor }}>
                      NAME
                    </th>
                    <th style={{ color: titleColor }}>POINTS</th>
                    <th style={{ color: titleColor }}>TIME</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="leaderboard-empty" style={{ color: cellColor }}>
                        No results yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => {
                      const isMe = meUserId && String(row.userId) === meUserId;
                      const isBelowTop5 = Number(row.rank ?? idx + 1) > 5;
                      const sr =
                        Number.isFinite(Number(row.rank)) && Number(row.rank) > 0
                          ? Number(row.rank)
                          : idx + 1;
                      const rowClass = [
                        isMe ? "leaderboard-row--current" : "",
                        isBelowTop5 ? "leaderboard-row--below-top5" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <tr key={`${sr}-${row.userId || idx}`} className={rowClass || undefined}>
                          <td className="leaderboard-table__rank" style={{ color: cellColor }}>
                            {sr}
                          </td>
                          <td className="leaderboard-table__name-col" style={{ color: cellColor }}>
                            {String(row.name ?? row.fullName ?? row.username ?? row.email ?? "—") || "—"}
                            {row.day != null || row.stage != null ? (
                              <div className="leaderboard-day-sub">
                                Day {row.day ?? row.stage}
                              </div>
                            ) : null}
                          </td>
                          <td className="leaderboard-table__points" style={{ color: cellColor }}>
                            {Number(row.points ?? 0) || 0}
                          </td>
                          <td className="leaderboard-table__time" style={{ color: cellColor }}>
                            {timeToMss(row.time)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
